<template>
  <div class="modal-backdrop-custom" @click.self="cerrar">
    <div class="modal-content-custom">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">
          <span class="material-icons align-middle me-1" style="font-size:18px">upload_file</span>
          Subir archivos PDF
        </h6>
        <button class="btn btn-sm btn-outline-secondary" @click="cerrar">
          <span class="material-icons" style="font-size:16px">close</span>
        </button>
      </div>

      <div
        class="dropzone"
        :class="{ 'dropzone-active': arrastrando }"
        @dragover.prevent="arrastrando = true"
        @dragleave.prevent="arrastrando = false"
        @drop.prevent="onDrop"
        @click="$refs.inputArchivo.click()"
      >
        <input
          ref="inputArchivo"
          type="file"
          class="d-none"
          accept="application/pdf,.pdf"
          multiple
          @change="onFileChange"
        />
        <div v-if="archivos.length === 0" class="text-center text-muted py-4">
          <span class="material-icons mb-1 d-block" style="font-size: 40px; opacity: 0.4">cloud_upload</span>
          <p class="mb-0 small">Arrastra uno o varios PDF aquí o haz clic para seleccionar</p>
        </div>
        <div v-else class="lista-archivos">
          <div
            v-for="(archivo, index) in archivos"
            :key="index"
            class="d-flex align-items-center justify-content-between py-2 px-2 archivo-item"
          >
            <div class="d-flex align-items-center gap-2 text-truncate">
              <span class="material-icons text-danger" style="font-size:20px">picture_as_pdf</span>
              <span class="text-truncate">{{ archivo.name }}</span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <span class="text-muted small">{{ formatearTamano(archivo.size) }}</span>
              <button
                type="button"
                class="btn btn-sm btn-link text-danger p-0"
                @click.stop="quitarArchivo(index)"
                :disabled="subiendo"
                title="Quitar"
              >
                <span class="material-icons" style="font-size:18px">close</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <p v-if="archivos.length > 0" class="text-muted small mt-2 mb-0">
        {{ archivos.length }} archivo{{ archivos.length > 1 ? 's' : '' }} seleccionado{{ archivos.length > 1 ? 's' : '' }}
        · {{ formatearTamano(tamanoTotal) }}
      </p>

      <div v-if="subiendo" class="progress mt-3" style="height: 6px">
        <div
          class="progress-bar"
          role="progressbar"
          :style="{ width: progreso + '%' }"
        ></div>
      </div>

      <div class="d-flex justify-content-end gap-2 mt-3">
        <button class="btn btn-sm btn-outline-secondary" @click="cerrar" :disabled="subiendo">
          Cancelar
        </button>
        <button
          class="btn btn-sm btn-primary"
          @click="subirArchivos"
          :disabled="archivos.length === 0 || subiendo"
        >
          {{ subiendo ? `Subiendo... ${progreso}%` : `Subir (${archivos.length})` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "ArchivoPlaUploadModal",

  props: {
    p_idTramite: { type: Number, default: null },
    p_idPlanilla: { type: Number, required: true },
    p_idCarga: { type: Number, default: null },
  },

  emits: ["cerrar", "cargado"],

  data() {
    return {
      archivos: [],
      subiendo: false,
      progreso: 0,
      arrastrando: false,
    };
  },

  computed: {
    tamanoTotal() {
      return this.archivos.reduce((acc, f) => acc + f.size, 0);
    },
  },

  methods: {
    onFileChange(event) {
      this.agregarArchivos(Array.from(event.target.files));
      event.target.value = ""; // permite volver a seleccionar el mismo archivo si se quitó
    },

    onDrop(event) {
      this.arrastrando = false;
      this.agregarArchivos(Array.from(event.dataTransfer.files));
    },

    agregarArchivos(nuevos) {
      const soloPDF = [];
      const rechazados = [];

      nuevos.forEach((file) => {
        const esPDF =
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".pdf");
        if (esPDF) {
          soloPDF.push(file);
        } else {
          rechazados.push(file.name);
        }
      });

      // Evita duplicados por nombre + tamaño
      soloPDF.forEach((file) => {
        const yaExiste = this.archivos.some(
          (f) => f.name === file.name && f.size === file.size
        );
        if (!yaExiste) this.archivos.push(file);
      });

      if (rechazados.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Archivos no permitidos",
          text: `Solo se aceptan PDF. Se ignoraron: ${rechazados.join(", ")}`,
          confirmButtonColor: "#f39c12",
        });
      }
    },

    quitarArchivo(index) {
      this.archivos.splice(index, 1);
    },

    formatearTamano(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },

    async subirArchivos() {
      if (this.archivos.length === 0) return;

      const formData = new FormData();
      this.archivos.forEach((file) => {
        formData.append("files", file);
      });
      formData.append("idPlanilla", this.p_idPlanilla);
      if (this.p_idTramite) formData.append("idTramite", this.p_idTramite);
      if (this.p_idCarga) formData.append("idCarga", this.p_idCarga);

      this.subiendo = true;
      this.progreso = 0;

      try {
        const { data } = await api.post("/archivospla/upload-multiple", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            this.progreso = Math.round((evt.loaded * 100) / evt.total);
          },
        });

        Swal.fire({
          icon: "success",
          title: "Archivos cargados",
          text: `Se subieron ${data.length} archivo(s) correctamente`,
          confirmButtonColor: "#0d6efd",
          timer: 1800,
          showConfirmButton: false,
        });

        this.$emit("cargado");
      } catch (err) {
        console.error("Error al subir archivos:", err);
        Swal.fire({
          icon: "error",
          title: "Error al subir",
          text: err.response?.data?.message || err.response?.data || "No se pudieron cargar los archivos",
          confirmButtonColor: "#dc3545",
        });
      } finally {
        this.subiendo = false;
      }
    },

    cerrar() {
      if (!this.subiendo) this.$emit("cerrar");
    },
  },
};
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.modal-content-custom {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  width: 450px;
  max-width: 90vw;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}
.dropzone {
  border: 2px dashed #ced4da;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  max-height: 240px;
  overflow-y: auto;
}
.dropzone:hover {
  border-color: #0d6efd;
  background: #f8f9ff;
}
.dropzone-active {
  border-color: #0d6efd;
  background: #eef4ff;
}
.lista-archivos {
  text-align: left;
}
.archivo-item {
  border-bottom: 1px solid #f0f0f0;
}
.archivo-item:last-child {
  border-bottom: none;
}
</style>